import { useState } from "react"
import {
    Typography,
    Button,
    Card,
    Table,
    message,
    Tag,
    Statistic,
} from "antd"
import {
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons"
import moment from "moment"

const { Title, Text } = Typography

// Helper for Ant Design message
const showMessage = (type, content) => {
    message[type](content)
}

const SystemStatus = () => {
    const [systemStatus, setSystemStatus] = useState("operational") // 'operational', 'paused', 'offline'
    const lastCheckTime = moment().format("MMMM D, YYYY HH:mm A")

    const getStatusCard = () => {
        switch (systemStatus) {
            case "operational":
                return (
                    <Card className="bg-green-50 border-green-200">
                        <div className="flex items-center">
                            <CheckCircleOutlined className="text-green-500 mr-2 text-lg" />
                            <Text strong className="text-green-700">
                                All systems operational
                            </Text>
                        </div>
                    </Card>
                )
            case "paused":
                return (
                    <Card className="bg-yellow-50 border-yellow-200">
                        <div className="flex items-center">
                            <ExclamationCircleOutlined className="text-yellow-500 mr-2 text-lg" />
                            <Text strong className="text-yellow-700">
                                Execution paused (admin approval required)
                            </Text>
                        </div>
                    </Card>
                )
            case "offline":
                return (
                    <Card className="bg-red-50 border-red-200">
                        <div className="flex items-center">
                            <CloseCircleOutlined className="text-red-500 mr-2 text-lg" />
                            <Text strong className="text-red-700">
                                1 integration offline
                            </Text>
                        </div>
                    </Card>
                )
            default:
                return null
        }
    }

    const integrationStatusData = [
        { key: "1", name: "Asana", status: "connected", lastSync: "2 mins ago", action: "View Logs" },
        { key: "2", name: "QuickBooks", status: "error", lastSync: "45 mins ago", action: "Reconnect" },
        { key: "3", name: "Slack", status: "delayed", lastSync: "10 mins ago", action: "Refresh" },
    ]

    const integrationColumns = [
        { title: "Integration", dataIndex: "name", key: "name" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => {
                let color = "blue"
                if (text === "connected") color = "green"
                if (text === "error") color = "red"
                if (text === "delayed") color = "orange"
                return <Tag color={color}>{text}</Tag>
            },
        },
        { title: "Last Sync", dataIndex: "lastSync", key: "lastSync" },
        {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (text) => (
                <Button type="link" size="small">
                    {text}
                </Button>
            ),
        },
    ]

    return (
        <Card className="mb-6">
            <div className="mb-4">
                <Title level={4}>System Status</Title>
                <Text type="secondary">Monitor the real-time health of Radar+ and your connected systems.</Text>
            </div>

            <Title level={5} className="mb-4">
                System Health Overview
            </Title>
            {getStatusCard()}
            <Text type="secondary" className="block mt-2 text-sm">
                Last system check: {lastCheckTime}
            </Text>

            <Title level={5} className="mt-6 mb-4">
                Live Diagnostics Panel
            </Title>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card>
                    <Statistic title="System Uptime" value="99.998%" suffix="(last 30 days)" />
                </Card>
                <Card>
                    <Statistic title="Signal Throughput" value={47} suffix="per day" />
                </Card>
                <Card>
                    <Statistic title="Auto-Exec Success" value="96%" suffix="last 7 days" />
                </Card>
                <Card>
                    <Statistic title="API Latency" value={320} suffix="ms avg." />
                </Card>
                <Card>
                    <Statistic title="Sync Queue Status" value={2} suffix="tasks pending" />
                </Card>
            </div>

            <Title level={5} className="mb-4">
                Integration Status Table
            </Title>
            <Table dataSource={integrationStatusData} columns={integrationColumns} pagination={false} />

            <Button className="mt-6" onClick={() => showMessage("info", "Opening system logs...")}>
                Open System Logs
            </Button>
        </Card>
    )
}

export default SystemStatus;